<template>
  <div id="author-evaluation-list">
    <!-- Formulaire -->
    <v-dialog
      v-model="isFormActive"
      touchless
      :right="!$vuetify.rtl"
      :width="$vuetify.breakpoint.smAndUp ? 500 : '100%'"
    >
      <evaluation-form
        v-model="item"
        :is-form-active="isFormActive"
        @refetch-data="fetchItems"
      >
        <template v-slot:form-title>
          <span class="font-weight-semibold text-base text--primary">
            {{ item.id ? $t('Modifier') : $t('Ajouter un profil auteur') }}
          </span>

          <v-spacer />

          <v-btn
            icon
            small
            @click="isFormActive = !isFormActive"
          >
            <v-icon size="22">
              {{ icons.mdiClose }}
            </v-icon>
          </v-btn>
        </template>

        <template #form-cancel>
          <v-btn
            :loading="loading"
            color="secondary"
            outlined
            type="reset"
            @click="isFormActive = !isFormActive"
          >
            {{ $t('Annuler') }}
          </v-btn>
        </template>
      </evaluation-form>
    </v-dialog>

    <!-- Liste -->
    <v-card>
      <v-card-title>
        {{ $t("Profil auteur") }}

        <v-chip
          outlined
          class="ml-2"
        >
          {{ totalItems }}
        </v-chip>
      </v-card-title>

      <v-divider class="mt-4" />

      <!-- Recherche + bouton -->
      <v-card-text class="d-flex align-center flex-wrap pb-0">
        <v-text-field
          v-model="searchQuery"
          :placeholder="$t('Rechercher')"
          outlined
          hide-details
          dense
          class="user-search me-3 mb-4"
        />

        <v-spacer />

        <v-btn
          color="primary"
          class="mb-4 me-3"
          @click.stop="openDialog(false)"
        >
          <v-icon>
            {{ icons.mdiPlus }}
          </v-icon>

          <span>
            {{ $t("Ajouter un profil auteur") }}
          </span>
        </v-btn>
      </v-card-text>

      <!-- Tableau -->
      <v-data-table
        v-model="selectedRows"
        class="fixed-table"
        dense
        :headers="tableColumns"
        :items="items"
        :options.sync="options"
        :page.sync="page"
        :server-items-length="totalItems"
        :loading="loading"
        hide-default-footer
        show-select
        :loading-text="$t('Chargement des éléments')"
        :no-data-text="$t('Pas de données')"
        :no-results-text="$t('Aucun résultat')"
        @page-count="pageCount = $event"
      >
        <!-- Caractéristique -->
        <template #[`item.caracteristic`]="{ item }">
          <span>
            {{
              item.caracteristic && item.caracteristic.length > 30
                ? `${item.caracteristic.substring(0, 30)}...`
                : item.caracteristic
            }}
          </span>

          <v-menu
            v-if="item.caracteristic && item.caracteristic.length > 30"
            offset-y
            max-width="400"
          >
            <template #activator="{ on, attrs }">
              <v-icon
                small
                class="ml-1"
                v-bind="attrs"
                v-on="on"
              >
                {{ icons.mdiInformationOutline }}
              </v-icon>
            </template>

            <v-card>
              <v-card-text>
                {{ item.caracteristic }}
              </v-card-text>
            </v-card>
          </v-menu>
        </template>

        <!-- Niveau d'accompagnement -->
        <template #[`item.level_of_support`]="{ item }">
          <span>
            {{
              item.level_of_support && item.level_of_support.length > 30
                ? `${item.level_of_support.substring(0, 30)}...`
                : item.level_of_support
            }}
          </span>

          <v-menu
            v-if="item.level_of_support && item.level_of_support.length > 30"
            offset-y
            max-width="400"
          >
            <template #activator="{ on, attrs }">
              <v-icon
                small
                class="ml-1"
                v-bind="attrs"
                v-on="on"
              >
                {{ icons.mdiInformationOutline }}
              </v-icon>
            </template>

            <v-card>
              <v-card-text>
                {{ item.level_of_support }}
              </v-card-text>
            </v-card>
          </v-menu>
        </template>

        <!-- Actions -->
        <template #[`item.actions`]="{ item }">
          <v-btn
            icon
            small
            @click="openDialog(item)"
          >
            <v-icon size="20">
              {{ icons.mdiPencilOutline }}
            </v-icon>
          </v-btn>

          <!--          <v-btn
                      icon
                      small
                      color="error"
                      @click="confirmDelete(item)"
                    >
                      <v-icon
                        size="20"
                        color="error"
                      >
                        {{ icons.mdiDeleteOutline }}
                      </v-icon>
                    </v-btn>-->
          <v-tooltip
            bottom
            :disabled="!item.authors_count"
          >
            <template #activator="{ on, attrs }">
              <span
                v-bind="attrs"
                class="d-inline-block"
                v-on="on"
              >
                <v-btn
                  icon
                  small
                  color="error"
                  :disabled="item.authors_count > 0"
                  @click="confirmDelete(item)"
                >
                  <v-icon
                    size="20"
                    :color="item.authors_count > 0 ? 'grey' : 'error'"
                  >
                    {{ icons.mdiDeleteOutline }}
                  </v-icon>
                </v-btn>
              </span>
            </template>

            <span>
              {{ $t("Ce profil ne peut pas être supprimé car il est déjà attribué à un auteur.") }}
            </span>
          </v-tooltip>
        </template>
      </v-data-table>

      <!-- Pagination -->
      <v-card-text class="pt-2">
        <v-row>
          <v-col
            lg="2"
            cols="3"
          >
            <rows-per-page :options="options" />
          </v-col>

          <v-col
            lg="10"
            cols="9"
            class="d-flex justify-end"
          >
            <v-pagination
              v-model="page"
              total-visible="6"
              :length="pageCount"
            />
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Confirmation suppression -->
    <v-dialog
      v-model="deleteDialog"
      touchless
      :right="!$vuetify.rtl"
      :width="$vuetify.breakpoint.smAndUp ? 300 : '100%'"
    >
      <v-card>
        <v-card-title>
          {{ $t("Confirmer la suppression") }}
        </v-card-title>

        <v-divider />

        <v-card-text>
          {{ $t("Confirmez-vous de vouloir supprimer") }}
        </v-card-text>

        <v-divider />

        <v-card-actions>
          <v-btn
            small
            outlined
            @click="deleteDialog = false"
          >
            {{ $t("Non") }}
          </v-btn>

          <v-spacer />

          <v-btn
            small
            color="primary"
            @click="deleteItem"
          >
            {{ $t("Oui") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar -->
    <v-snackbar
      v-model="isSnackbarBottomVisible"
      :color="$vuetify.theme.currentTheme.error"
      :light="$vuetify.theme.dark"
    >
      {{ snackBarMessage }}
    </v-snackbar>
  </div>
</template>

<script>
import {
  mdiPlus,
  mdiClose,
  mdiDeleteOutline,
  mdiPencilOutline,
  mdiInformationOutline,
} from '@mdi/js'

import { onUnmounted } from '@vue/composition-api'

import store from '@/store'

import EvaluationForm from './EvaluationForm.vue'
import evaluationStoreModule from './itemStoreModule'
import controller from './Controller'
import RowsPerPage from '@/components/RowsPerPage'

export default {
  components: {
    RowsPerPage,
    EvaluationForm,
  },

  setup() {
    const APP_STORE_MODULE_NAME = 'authorEvaluation'

    if (!store.hasModule(APP_STORE_MODULE_NAME)) {
      store.registerModule(
        APP_STORE_MODULE_NAME,
        evaluationStoreModule,
      )
    }

    onUnmounted(() => {
      if (store.hasModule(APP_STORE_MODULE_NAME)) {
        store.unregisterModule(APP_STORE_MODULE_NAME)
      }
    })

    const {
      item,
      items,
      tableColumns,
      searchQuery,
      totalItems,
      loading,
      options,
      selectedRows,
      isFormActive,
      snackBarMessage,
      isSnackbarBottomVisible,
      openDialog,
      deleteItem,
      confirmDelete,
      deleteDialog,
      fetchItems,
    } = controller()

    return {
      item,
      items,
      tableColumns,
      searchQuery,
      totalItems,
      loading,
      options,
      isFormActive,
      selectedRows,
      snackBarMessage,
      isSnackbarBottomVisible,
      page: 1,
      pageCount: 0,
      itemsPerPage: 10,
      deleteDialog,
      confirmDelete,
      openDialog,
      deleteItem,
      fetchItems,

      icons: {
        mdiDeleteOutline,
        mdiPlus,
        mdiClose,
        mdiPencilOutline,
        mdiInformationOutline,
      },
    }
  },
}
</script>

<style lang="scss">
@import '@core/preset/preset/apps/user.scss';
</style>
